<template>
  <div class="sd-faste-tillaeg text-dark">
    <h2 class="marg-1-bottom capitalize">Faste tillæg</h2>
    <template v-if="isNoConfiguration">
      <div class="sd-faste-tillaeg-no-config">
        <h2>
          <i>Skolens Silkeborg Data-integration er ikke konfigureret korrekt. Kontakt Docendo for at få hjælp til
            opsætningen - enten på chat eller email.</i>
        </h2>
      </div>
    </template>
    <template v-else>
      <div class="sd-faste-tillaeg__header">
        <div class="sd-faste-tillaeg__header__left">
          <Tabs
            :tabs="viewTabs"
            :selected="viewTab"
            @select="onSelectViewTab">
          </Tabs>
          <template v-if="viewTab === 'employments'">
            <DoFiltersFilter
              :title="'Personer'"
              :tags="personNames"
              :selectedTags="selectedPersonNames"
              :numberOfSelectedTagsToShow="1"
              :placeholder="'Vælg personer'"
              @select="togglePersonName"
              @clear="selectedPersonNames = []">
            </DoFiltersFilter>
            <DoFiltersFilter
              :title="'Grupper'"
              :tags="userTags"
              :selectedTags="selectedUserTags"
              :numberOfSelectedTagsToShow="1"
              :placeholder="'Vælg grupper'"
              @select="toggleUserTag"
              @clear="selectedUserTags = []">
            </DoFiltersFilter>
            <DoFiltersFilter
              v-if="employmentTitles.length > 1"
              :title="'Ansættelser'"
              :tags="employmentTitles"
              :selectedTags="selectedEmploymentTitles"
              :numberOfSelectedTagsToShow="1"
              :placeholder="'Vælg stilling'"
              @select="toggleEmploymentTitle"
              @clear="selectedEmploymentTitles = []">
            </DoFiltersFilter>
            <DoFiltersFilter
              v-if="organisations.length > 1"
              :title="'Organisationer'"
              :tags="organisations"
              :selectedTags="selectedOrganisations"
              :numberOfSelectedTagsToShow="1"
              :placeholder="'Vælg organisation'"
              @select="toggleOrganisation"
              @clear="selectedOrganisations = []">
            </DoFiltersFilter>
            <span class="sd-faste-tillaeg__count">
              Viser {{ filteredRows.length }} af {{ allRows.length }} ansættelser
            </span>
          </template>
          <template v-else>
            <DoFiltersFilter
              :title="'Personer'"
              :tags="uniqueNames"
              :selectedTags="selectedNames"
              :numberOfSelectedTagsToShow="2"
              :placeholder="'Vælg personer'"
              @select="toggleName"
              @clear="selectedNames = []">
            </DoFiltersFilter>
            <DoFiltersFilter
              :title="'Lønkode'"
              :tags="uniqueTillaeg"
              :selectedTags="selectedTillaeg"
              :numberOfSelectedTagsToShow="1"
              :placeholder="'Vælg lønkode'"
              @select="toggleTillaeg"
              @clear="selectedTillaeg = []">
            </DoFiltersFilter>
          </template>
        </div>
        <div class="sd-faste-tillaeg__header__right" v-if="viewTab !== 'employments'">
          <button class="do-bttn do-bttn--small" :disabled="isLoading" @click="readTillaeger">
            Opdater
          </button>
        </div>
      </div>

      <!-- Værktøjslinje: Alle ansættelser (opret) -->
      <div class="sd-faste-tillaeg__toolbar" v-if="viewTab === 'employments'">
        <span class="sd-faste-tillaeg__toolbar__wrap"
              :content="selectedEmploymentIds.length === 0 ? 'Vælg personer først' : ''"
              v-tippy="{
                arrow: true,
                placement: 'top',
                animateFill: false,
                animation: 'shift-away',
                boundary: 'window'
              }">
          <button class="do-bttn do-bttn--submit"
                  :class="{'do-bttn--submit--disabled': selectedEmploymentIds.length === 0}"
                  :disabled="selectedEmploymentIds.length === 0"
                  @click="openBulkCreate">
            Opret faste tillæg
          </button>
        </span>
        <DDropdown :isBottomStart="true" :isShowDots="false" :propOffset="[0, 8]">
          <button slot="title" class="do-bttn" style="font-size: 11px;">
            Beregn tillæg automatisk <span class="icon-arrow-down5"></span>
          </button>
          <ul slot="list" class="beregn-menu">
            <li v-for="option in visibleBeregnOptions"
                :key="option.id"
                @click="selectBeregnOption(option)">
              {{ option.label }}
            </li>
          </ul>
        </DDropdown>
        <button class="do-bttn" v-if="selectedEmploymentIds.length > 0" @click="cancelBatchMode">Annuller valg</button>
        <span class="sd-faste-tillaeg__count" v-if="selectedEmploymentIds.length > 0">
          {{ selectedEmploymentIds.length }} valgt
        </span>
      </div>

      <!-- Info: Udbakke (sendes automatisk) -->
      <div class="sd-faste-tillaeg__info" v-if="viewTab === 'outbox'">
        Faste tillæg i udbakken sendes automatisk til SD senere på dagen. Du kan slette dem, så længe de ligger her.
      </div>

      <div class="sd-faste-tillaeg__body">
        <!-- Tab: Alle ansættelser -->
        <template v-if="viewTab === 'employments'">
          <Loader v-if="isLoadingEmployments"></Loader>
          <table v-else class="do-table">
            <thead>
            <tr>
              <th class="td-check do-table__toggle-all" @click="toggleAllSelected">
                <div class="do-checkbox" :class="{'do-checkbox--selected': isAllFilteredSelected}"></div>
              </th>
              <th>NAVN</th>
              <th>ANSÆTTELSE</th>
              <th>ORGANISATION</th>
              <th class="th-row-actions"></th>
            </tr>
            </thead>
            <tbody v-if="filteredRows.length === 0">
            <tr>
              <td :colspan="5" class="italic">Ingen ansættelser fundet</td>
            </tr>
            </tbody>
            <tbody v-for="group in groupedRows" :key="group.userId" class="user-group">
            <tr v-for="row in group.rows"
                :key="row.id"
                class="fast-tillaeg-row"
                :class="{'is-selected': isRowSelected(row)}"
                @click="onRowClick(row)">
              <td class="td-check" @click.stop="onCheckboxClick(row)">
                <div class="do-checkbox" :class="{'do-checkbox--selected': isRowSelected(row)}"></div>
              </td>
              <td class="bold">{{ row.userName }}</td>
              <td class="td-organisation">
                {{ row.employmentNumber }} - {{ row.employmentTitle }}
              </td>
              <td class="td-organisation">{{ row.organisation }}</td>
              <td class="td-row-actions">
                <button
                  v-if="!isBatchMode"
                  type="button"
                  class="do-bttn do-bttn--small row-action-btn"
                  @click.stop="openSingleCreate(row)"
                >Opret fast tillæg</button>
              </td>
            </tr>
            </tbody>
          </table>
        </template>

        <!-- Tabs: Udbakke / Sendt -->
        <template v-else>
          <Loader v-if="isLoading"></Loader>
          <table v-else class="do-table do-table--striped">
            <thead>
            <tr>
              <th class="sortable td-created" @click="sortEntries('created_at')">OPRETTET <span class="icon-menu-open sort-icon"></span></th>
              <th class="sortable" @click="sortEntries('user')">NAVN <span class="icon-menu-open sort-icon"></span></th>
              <th class="sortable" @click="sortEntries('employment')">ANSÆTTELSE <span class="icon-menu-open sort-icon"></span></th>
              <th class="sortable" @click="sortEntries('tillaeg')">TILLÆG <span class="icon-menu-open sort-icon"></span></th>
              <th class="sortable td-units" @click="sortEntries('antal')">ANTAL <span class="icon-menu-open sort-icon"></span></th>
              <th class="sortable td-date" @click="sortEntries('fra')">FRA <span class="icon-menu-open sort-icon"></span></th>
              <th class="sortable td-date" @click="sortEntries('til')">TIL <span class="icon-menu-open sort-icon"></span></th>
              <th class="sortable td-created-by" @click="sortEntries('created_by')">OPRETTET AF <span class="icon-menu-open sort-icon"></span></th>
              <th class="sortable td-status" @click="sortEntries('state')">STATUS <span class="icon-menu-open sort-icon"></span></th>
              <th class="th-actions"></th>
            </tr>
            </thead>
            <tbody>
            <tr v-if="displayedPairs.length === 0">
              <td :colspan="10" class="italic">
                {{ viewTab === 'outbox' ? 'Udbakken er tom' : 'Ingen afsendte faste tillæg' }}
              </td>
            </tr>
            <tr v-for="pair in displayedPairs" :key="pair.vRowKey" class="tillaeg-row">
              <td class="td-created">{{ pair.vCreatedAt }}</td>
              <td>{{ pair.vUserName }}</td>
              <td>{{ pair.vEmploymentNumber }}</td>
              <td>{{ pair.vTillaeg }}</td>
              <td class="td-units">{{ pair.vEnheder }}</td>
              <td class="td-date">{{ pair.vFraDato }}</td>
              <td class="td-date">{{ pair.vTilDato }}</td>
              <td class="td-created-by">{{ pair.vCreatedBy }}</td>
              <td class="td-status">
                <span class="status-pill" :class="'status-pill--' + pair.state">{{ stateLabel(pair.state) }}</span>
              </td>
              <td class="td-actions">
                <button v-if="viewTab === 'outbox' && pair.can_delete"
                        class="do-bttn do-bttn--small do-bttn--danger"
                        :disabled="isDeleting(pair)"
                        @click.stop="deletePair(pair)">
                  Slet
                </button>
              </td>
            </tr>
            </tbody>
          </table>
        </template>
      </div>
    </template>

    <SdCreateFastTillaeg
      v-if="createEmploymentIds.length > 0"
      :schoolId="schoolId"
      :employmentIds="createEmploymentIds"
      :subtitle="createSubtitle"
      @created="onCreated"
      @close="closeCreate">
    </SdCreateFastTillaeg>

    <TidsbegraensetUvModal
      v-if="isShowTidsbegraenset"
      @created="isShowTidsbegraenset = false"
      @close="isShowTidsbegraenset = false">
    </TidsbegraensetUvModal>

    <DModal
      v-if="isShowBeregnModal"
      :show-close="false"
      :isBgOverlay="true"
      :isCloseOnBgClick="false"
      @close="closeBeregnModal"
      :appendToBody="true"
      style="z-index: 12312312312313"
      :placeInTop="true">
      <div class="beregn-modal text-dark" slot="insertComponent">
        <div class="beregn-modal__header">
          <h2>{{ beregnModalTitle }}</h2>
          <div class="font-large icon__bg-circle beregn-modal__close" @click="closeBeregnModal">
            <span class="icon-cross2"></span>
          </div>
        </div>
        <div class="beregn-modal__content">
          <Supplements
            :isInOpusModal="true"
            :beregningType="beregnType"
            :sendToOpusTrigger="sendToSdTrigger"
            @approveState="sdApproveState = $event">
          </Supplements>
        </div>
        <div class="beregn-modal__footer" v-if="sdApproveState.canApprove">
          <span class="send-to-sd-wrap"
                :content="sendToSdTooltip"
                v-tippy="{
                  arrow: true,
                  placement: 'top',
                  animateFill: false,
                  animation: 'shift-away',
                  boundary: 'window',
                  onShow: () => sendToSdTooltip !== ''
                }">
            <button class="do-bttn do-bttn--submit"
                    :class="{'do-bttn--submit--loading': sdApproveState.isApproving, 'do-bttn--submit--disabled': sdApproveState.count === 0 || sdApproveState.missingEmployment}"
                    :disabled="sdApproveState.isApproving || sdApproveState.count === 0 || sdApproveState.missingEmployment"
                    @click="triggerSendToSd">
              Send til SD
            </button>
          </span>
        </div>
      </div>
    </DModal>
  </div>
</template>

<script>
import docendo from '../../../docendo'
import eventbus from '../../../eventbus'
import Loader from '../../common/Loader.vue'
import moment from 'moment'
import DoFiltersFilter from '../../DoFilters/DoFiltersFilter.vue'
import SdCreateFastTillaeg from './SdCreateFastTillaeg.vue'
import Tabs from '../../common/Tabs.vue'
import DModal from '../../common/DModal.vue'
import DDropdown from '../../common/DDropdown.vue'
import Supplements from '../../Supplements/Supplements.vue'
import TidsbegraensetUvModal from '../../Supplements/TidsbegraensetUvModal.vue'

export default {
  name: 'SdFasteTillaeg',
  components: {Loader, DoFiltersFilter, SdCreateFastTillaeg, Tabs, DModal, DDropdown, Supplements, TidsbegraensetUvModal},
  data () {
    return {
      isNoConfiguration: false,
      isLoading: false,
      tillaeger: [],
      loenaendringer: [],
      selectedNames: [],
      selectedTillaeg: [],
      viewTab: 'employments',
      selectedPersonNames: [],
      selectedUserTags: [],
      selectedEmploymentTitles: [],
      selectedOrganisations: [],
      selectedEmploymentIds: [],
      isBatchMode: false,
      createEmploymentIds: [],
      createSubtitle: '',
      deletingKeys: [],
      entriesSortKey: 'created_at',
      entriesSortDir: 'desc',
      isShowBeregnModal: false,
      isShowTidsbegraenset: false,
      beregnType: null,
      sendToSdTrigger: 0,
      sdApproveState: {count: 0, isApproving: false, canApprove: false, missingEmployment: false},
      beregnOptions: [
        {id: 'loenbroek', label: 'Lønbrøk'},
        {id: 'uv_over_graense', label: 'Undervisningstillæg – timer over grænse (§5 stk. 2)'},
        {id: 'spec_skole_uv', label: 'Specialundervisning (§ 5, stk. 4)'},
        {id: 'dsa_tale_hoere', label: 'DSA og tale/høre-undervisning (§5 stk. 9 og 10)'},
        {id: 'paedagog_eneansvar', label: 'Pædagog med eneansvar'}
      ]
    }
  },
  computed: {
    viewTabs () {
      return [
        {id: 'employments', title: 'Oprettelse'},
        {id: 'outbox', title: `Udbakke (${this.outboxCount})`},
        {id: 'sent', title: `Sendt til lønsystem (${this.sentCount})`}
      ]
    },
    outboxCount () {
      return this.allEntries.filter(p => p.state !== 'sent').length
    },
    sentCount () {
      return this.allEntries.filter(p => p.state === 'sent').length
    },
    schoolId () {
      return this.$store.getters.schoolId
    },
    isLoadingEmployments () {
      return this.$store.state.employments && this.$store.state.employments.isLoadingEmploymentsTypesForSchool
    },
    users () {
      return this.$store.getters.usersSortedOnName || []
    },
    usersById () {
      const map = {}
      const list = this.users
      for (let i = 0; i < list.length; i++) map[list[i].id] = list[i].name
      return map
    },
    employmentsInUserIdHash () {
      return this.$store.getters.employmentsInUserIdHash || {}
    },
    allRows () {
      const out = []
      for (let i = 0; i < this.users.length; i++) {
        const user = this.users[i]
        const emps = this.employmentsInUserIdHash[user.id] || []
        for (let j = 0; j < emps.length; j++) {
          const emp = emps[j]
          const empNumber = emp && emp.employment_number ? String(emp.employment_number) : ''
          if (empNumber.trim() === '') continue
          if (emp.source === 'opus') continue
          // Kun ansættelser med en stillingskategori kan vælges til faste tillæg.
          if (emp.employment_template_id == null) continue
          const title = emp.source_title || emp.title || 'Ansættelse'
          out.push({
            id: user.id + '_' + emp.id,
            userId: user.id,
            userName: user.name,
            userTags: Array.isArray(user.user_tags) ? user.user_tags : [],
            employmentId: emp.id,
            employmentTitle: title,
            employmentNumber: empNumber,
            organisation: emp.organisation || '',
            employment: emp
          })
        }
      }
      return out.sort((a, b) => a.userName.localeCompare(b.userName, 'da-DK', {sensitivity: 'base'}))
    },
    personNames () {
      const set = {}
      for (let i = 0; i < this.allRows.length; i++) if (this.allRows[i].userName) set[this.allRows[i].userName] = true
      return Object.keys(set).sort((a, b) => a.localeCompare(b, 'da-DK'))
    },
    userTags () {
      const set = {}
      for (let i = 0; i < this.allRows.length; i++) {
        const t = this.allRows[i].userTags || []
        for (let j = 0; j < t.length; j++) if (t[j]) set[t[j]] = true
      }
      return Object.keys(set).sort((a, b) => a.localeCompare(b, 'da-DK'))
    },
    employmentTitles () {
      const set = {}
      for (let i = 0; i < this.allRows.length; i++) if (this.allRows[i].employmentTitle) set[this.allRows[i].employmentTitle] = true
      return Object.keys(set).sort((a, b) => a.localeCompare(b, 'da-DK'))
    },
    organisations () {
      const set = {}
      for (let i = 0; i < this.allRows.length; i++) if (this.allRows[i].organisation) set[this.allRows[i].organisation] = true
      return Object.keys(set).sort((a, b) => a.localeCompare(b, 'da-DK'))
    },
    filteredRows () {
      return this.allRows.filter(row => {
        if (this.selectedPersonNames.length > 0 && this.selectedPersonNames.indexOf(row.userName) === -1) return false
        if (this.selectedUserTags.length > 0) {
          const match = row.userTags && this.selectedUserTags.some(t => row.userTags.indexOf(t) > -1)
          if (!match) return false
        }
        if (this.selectedEmploymentTitles.length > 0 && this.selectedEmploymentTitles.indexOf(row.employmentTitle) === -1) return false
        if (this.selectedOrganisations.length > 0 && this.selectedOrganisations.indexOf(row.organisation) === -1) return false
        return true
      })
    },
    isAllFilteredSelected () {
      if (this.filteredRows.length === 0) return false
      return this.filteredRows.every(row => this.selectedEmploymentIds.indexOf(row.employmentId) > -1)
    },
    groupedRows () {
      const groups = []
      const indexByUser = {}
      for (let i = 0; i < this.filteredRows.length; i++) {
        const row = this.filteredRows[i]
        if (indexByUser[row.userId] === undefined) {
          indexByUser[row.userId] = groups.length
          groups.push({userId: row.userId, rows: []})
        }
        groups[indexByUser[row.userId]].rows.push(row)
      }
      return groups
    },
    employmentsById () {
      const map = {}
      const list = (this.$store.state.employments && this.$store.state.employments.employments) || []
      for (let i = 0; i < list.length; i++) map[list[i].id] = list[i]
      return map
    },
    salaryCodes () {
      return this.$store.getters.salaryCodesSorted || []
    },
    // Specialundervisning (§ 5, stk. 4) kan kun beregnes hvis den tilhørende lønkode er oprettet.
    // SD: tillægsnummer 160099/160100 eller lønart 836.
    hasSpecialSalaryCode () {
      return this.salaryCodes.some(salaryCode =>
        String(salaryCode.tillaegsnummer) === '160099' ||
        String(salaryCode.tillaegsnummer) === '160100' ||
        String(salaryCode.loenart) === '836')
    },
    visibleBeregnOptions () {
      return this.beregnOptions
        // Spec-skole uv-tillæg (§ 5, stk. 4) er midlertidigt skjult for SD.
        .filter(option => option.id !== 'spec_skole_uv')
    },
    beregnModalTitle () {
      const option = this.beregnOptions.find(o => o.id === this.beregnType)
      return option ? option.label : 'Beregn tillæg automatisk'
    },
    sendToSdTooltip () {
      if (this.sdApproveState.count === 0) return 'Opret lønlinjer først'
      if (this.sdApproveState.missingEmployment) return 'Vælg ansættelse på alle linjer'
      return ''
    },
    salaryCodesById () {
      const map = {}
      const codes = this.salaryCodes || []
      for (let i = 0; i < codes.length; i++) map[codes[i].id] = codes[i]
      return map
    },
    salaryCodesByTillaegsnummer () {
      const map = {}
      const codes = this.salaryCodes || []
      for (let i = 0; i < codes.length; i++) {
        if (codes[i].tillaegsnummer != null) map[String(codes[i].tillaegsnummer)] = codes[i]
      }
      return map
    },
    // Parrer start- (O) og stop-linjer (S) der hører sammen, så de kan vises på én række.
    allPairs () {
      const groups = {}
      for (let i = 0; i < this.tillaeger.length; i++) {
        const t = this.tillaeger[i]
        const empKey = t.employment_id != null ? 'e' + t.employment_id : 'n' + t.tjenestenummer
        const key = empKey + '|' + (t.tillaegsnummer != null ? t.tillaegsnummer : '') + '|' + (t.salary_code_id != null ? t.salary_code_id : '')
        if (!groups[key]) groups[key] = {o: [], s: []}
        if (t.aendringskode === 'S') groups[key].s.push(t)
        else groups[key].o.push(t)
      }
      const pairs = []
      Object.keys(groups).forEach(key => {
        const g = groups[key]
        g.o.sort((a, b) => a.id - b.id)
        g.s.sort((a, b) => a.id - b.id)
        const count = Math.max(g.o.length, g.s.length)
        for (let i = 0; i < count; i++) {
          pairs.push(this.buildPair(g.o[i] || null, g.s[i] || null))
        }
      })
      return pairs.sort((a, b) => {
        const av = a.created_at ? moment(a.created_at).valueOf() : 0
        const bv = b.created_at ? moment(b.created_at).valueOf() : 0
        return bv - av
      })
    },
    loenaendringRows () {
      return this.loenaendringer.map(loenaendring => this.buildLoenaendringRow(loenaendring))
    },
    // Faste tillæg og lønbrøker vises samlet i udbakke/afsendt, sorteret efter oprettelsestidspunkt.
    allEntries () {
      return this.allPairs.concat(this.loenaendringRows).sort((a, b) => {
        const av = a.created_at ? moment(a.created_at).valueOf() : 0
        const bv = b.created_at ? moment(b.created_at).valueOf() : 0
        return bv - av
      })
    },
    uniqueNames () {
      const set = {}
      for (let i = 0; i < this.allEntries.length; i++) {
        const name = this.allEntries[i].vUserName || ''
        if (name) set[name] = true
      }
      return Object.keys(set).sort((a, b) => a.localeCompare(b, 'da', {sensitivity: 'base'}))
    },
    uniqueTillaeg () {
      const set = {}
      for (let i = 0; i < this.allEntries.length; i++) {
        const tillaeg = this.allEntries[i].vTillaeg || ''
        if (tillaeg) set[tillaeg] = true
      }
      return Object.keys(set).sort((a, b) => a.localeCompare(b, 'da', {sensitivity: 'base', numeric: true}))
    },
    displayedPairs () {
      let rows = this.allEntries.slice()
      rows = rows.filter(p => this.viewTab === 'sent' ? p.state === 'sent' : p.state !== 'sent')
      if (this.selectedNames.length > 0) {
        const chosen = {}
        for (let i = 0; i < this.selectedNames.length; i++) chosen[this.selectedNames[i]] = true
        rows = rows.filter(p => chosen[p.vUserName || ''])
      }
      if (this.selectedTillaeg.length > 0) {
        const chosen = {}
        for (let i = 0; i < this.selectedTillaeg.length; i++) chosen[this.selectedTillaeg[i]] = true
        rows = rows.filter(p => chosen[p.vTillaeg || ''])
      }
      const dir = this.entriesSortDir === 'asc' ? 1 : -1
      const key = this.entriesSortKey
      return rows.sort((a, b) => {
        const av = this.entrySortValue(a, key)
        const bv = this.entrySortValue(b, key)
        let cmp
        if (typeof av === 'number' && typeof bv === 'number') {
          cmp = av - bv
        } else {
          cmp = String(av).localeCompare(String(bv), 'da-DK', {sensitivity: 'base'})
        }
        return cmp * dir
      })
    }
  },
  methods: {
    sortEntries (key) {
      if (this.entriesSortKey === key) {
        this.entriesSortDir = this.entriesSortDir === 'asc' ? 'desc' : 'asc'
      } else {
        this.entriesSortKey = key
        this.entriesSortDir = 'asc'
      }
    },
    entrySortValue (row, key) {
      switch (key) {
        case 'created_at': return row.created_at ? moment(row.created_at).valueOf() : 0
        case 'user': return (row.vUserName || '').toLowerCase()
        case 'employment': return (row.vEmploymentNumber || '').toLowerCase()
        case 'tillaeg': return (row.vTillaeg || '').toLowerCase()
        case 'antal': return row.vEnheder !== '' ? parseFloat(String(row.vEnheder).replace(',', '.')) || 0 : 0
        case 'fra': return row.fraDato ? moment(row.fraDato).valueOf() : 0
        case 'til': return row.tilDato ? moment(row.tilDato).valueOf() : 0
        case 'created_by': return (row.vCreatedBy || '').toLowerCase()
        case 'state': return this.stateLabel(row.state)
        default: return ''
      }
    },
    // Slår navnet op på den bruger (approved_by) der oprettede tillægget/lønbrøken.
    // Findes brugeren ikke i skolens brugerliste, vises selve id'et.
    createdByName (approvedBy) {
      if (approvedBy == null) return ''
      return this.usersById[approvedBy] || String(approvedBy)
    },
    buildPair (oLine, sLine) {
      const base = oLine || sLine
      const employment = base.employment_id ? this.employmentsById[base.employment_id] : null
      const userId = (employment && employment.user_id) || base.user_id
      const vUserName = (userId && this.usersById[userId]) ||
        (base.tjenestenummer ? String(base.tjenestenummer) : 'Ukendt')
      const vEmploymentNumber = employment && employment.employment_number
        ? employment.employment_number + (employment.title ? ' - ' + employment.title : '')
        : (base.tjenestenummer ? String(base.tjenestenummer) : 'Ukendt')
      const salaryCode = (base.salary_code_id && this.salaryCodesById[base.salary_code_id]) ||
        (base.tillaegsnummer != null ? this.salaryCodesByTillaegsnummer[String(base.tillaegsnummer)] : null)
      const tillaegLabel = base.tillaegsnummer != null ? String(base.tillaegsnummer) : ''
      const vTillaeg = salaryCode && salaryCode.name ? tillaegLabel + ' - ' + salaryCode.name : tillaegLabel
      const ids = []
      if (oLine) ids.push(oLine.id)
      if (sLine) ids.push(sLine.id)
      const state = base.state || 'draft'
      const canDelete = base.can_delete != null ? base.can_delete : (state === 'draft')
      return {
        vRowKey: 'p_' + (oLine ? oLine.id : 'x') + '_' + (sLine ? sLine.id : 'x'),
        kind: 'tillaeg',
        ids,
        state,
        can_delete: canDelete,
        created_at: base.created_at,
        vCreatedAt: base.created_at ? moment(base.created_at).format('lll') : '',
        vCreatedBy: this.createdByName(base.approved_by),
        vUserName,
        vEmploymentNumber,
        vTillaeg,
        vEnheder: this.formatDecimal(oLine ? oLine.enheder : (sLine ? sLine.enheder : null)),
        fraDato: oLine ? oLine.dato : null,
        tilDato: sLine ? sLine.dato : null,
        vFraDato: oLine && oLine.dato ? moment(oLine.dato).format('DD-MM-YYYY') : '',
        vTilDato: sLine && sLine.dato ? moment(sLine.dato).format('DD-MM-YYYY') : ''
      }
    },
    // Bygger en visningsrække for en lønbrøk (SD-lønændring). En lønbrøk er én linje
    // (ingen start/stop-parring), og har kun en startdato. "Antal" viser beskæftigelses-/
    // løndecimalen, eller "1" (fuldtid) når deltidskode er 0 og ingen decimal er sat.
    buildLoenaendringRow (loenaendring) {
      const employment = loenaendring.employment_id ? this.employmentsById[loenaendring.employment_id] : null
      const userId = (employment && employment.user_id) || loenaendring.user_id
      const vUserName = (userId && this.usersById[userId]) ||
        (loenaendring.tjenestenummer ? String(loenaendring.tjenestenummer) : 'Ukendt')
      const vEmploymentNumber = employment && employment.employment_number
        ? employment.employment_number + (employment.title ? ' - ' + employment.title : '')
        : (loenaendring.tjenestenummer ? String(loenaendring.tjenestenummer) : 'Ukendt')
      const state = loenaendring.state || 'draft'
      let vEnheder = ''
      if (loenaendring.beskaeftigelsesdecimal != null) vEnheder = this.formatDecimal(loenaendring.beskaeftigelsesdecimal)
      else if (loenaendring.loendecimal != null) vEnheder = this.formatDecimal(loenaendring.loendecimal)
      else if (loenaendring.aarstimetal != null) vEnheder = this.formatDecimal(loenaendring.aarstimetal)
      else if (Number(loenaendring.deltidskode) === 0) vEnheder = '1'
      return {
        vRowKey: 'l_' + loenaendring.id,
        kind: 'loenaendring',
        ids: [loenaendring.id],
        state,
        can_delete: state === 'draft',
        created_at: loenaendring.created_at,
        vCreatedAt: loenaendring.created_at ? moment(loenaendring.created_at).format('lll') : '',
        vCreatedBy: this.createdByName(loenaendring.approved_by),
        vUserName,
        vEmploymentNumber,
        vTillaeg: 'Lønbrøk',
        vEnheder,
        fraDato: loenaendring.dato,
        tilDato: null,
        vFraDato: loenaendring.dato ? moment(loenaendring.dato).format('DD-MM-YYYY') : '',
        vTilDato: ''
      }
    },
    onSelectViewTab (tab) {
      this.viewTab = tab
      if (tab !== 'employments' && !this.isLoading) this.readTillaeger()
    },
    togglePersonName (name) {
      const idx = this.selectedPersonNames.indexOf(name)
      if (idx > -1) this.selectedPersonNames.splice(idx, 1)
      else this.selectedPersonNames.push(name)
    },
    toggleUserTag (tag) {
      const idx = this.selectedUserTags.indexOf(tag)
      if (idx > -1) this.selectedUserTags.splice(idx, 1)
      else this.selectedUserTags.push(tag)
    },
    toggleEmploymentTitle (title) {
      const idx = this.selectedEmploymentTitles.indexOf(title)
      if (idx > -1) this.selectedEmploymentTitles.splice(idx, 1)
      else this.selectedEmploymentTitles.push(title)
    },
    toggleOrganisation (organisation) {
      const idx = this.selectedOrganisations.indexOf(organisation)
      if (idx > -1) this.selectedOrganisations.splice(idx, 1)
      else this.selectedOrganisations.push(organisation)
    },
    toggleName (name) {
      const idx = this.selectedNames.indexOf(name)
      if (idx > -1) this.selectedNames.splice(idx, 1)
      else this.selectedNames.push(name)
    },
    toggleTillaeg (tillaeg) {
      const idx = this.selectedTillaeg.indexOf(tillaeg)
      if (idx > -1) this.selectedTillaeg.splice(idx, 1)
      else this.selectedTillaeg.push(tillaeg)
    },
    isRowSelected (row) {
      return this.selectedEmploymentIds.indexOf(row.employmentId) > -1
    },
    toggleSelection (row) {
      const idx = this.selectedEmploymentIds.indexOf(row.employmentId)
      if (idx > -1) this.selectedEmploymentIds.splice(idx, 1)
      else this.selectedEmploymentIds.push(row.employmentId)
    },
    toggleAllSelected () {
      this.isBatchMode = true
      const visibleIds = this.filteredRows.map(row => row.employmentId)
      if (this.isAllFilteredSelected) {
        this.selectedEmploymentIds = this.selectedEmploymentIds.filter(id => visibleIds.indexOf(id) === -1)
      } else {
        const set = {}
        for (let i = 0; i < this.selectedEmploymentIds.length; i++) set[this.selectedEmploymentIds[i]] = true
        for (let i = 0; i < visibleIds.length; i++) set[visibleIds[i]] = true
        this.selectedEmploymentIds = Object.keys(set).map(id => parseInt(id, 10))
      }
    },
    cancelBatchMode () {
      this.isBatchMode = false
      this.selectedEmploymentIds = []
    },
    onRowClick (row) {
      if (this.isBatchMode) this.toggleSelection(row)
      else this.openSingleCreate(row)
    },
    onCheckboxClick (row) {
      if (!this.isBatchMode) this.isBatchMode = true
      this.toggleSelection(row)
    },
    openSingleCreate (row) {
      this.createEmploymentIds = [row.employmentId]
      this.createSubtitle = row.userName + ' – ' + row.employmentNumber + ' - ' + row.employmentTitle
    },
    openBulkCreate () {
      if (this.selectedEmploymentIds.length === 0) return
      this.createEmploymentIds = this.selectedEmploymentIds.slice()
      this.createSubtitle = ''
    },
    closeCreate () {
      this.createEmploymentIds = []
      this.createSubtitle = ''
    },
    onCreated () {
      this.closeCreate()
      this.cancelBatchMode()
      this.viewTab = 'outbox'
      this.readTillaeger()
    },
    isDeleting (pair) {
      return this.deletingKeys.includes(pair.vRowKey)
    },
    deletePair (pair) {
      if (pair.ids.length === 0 || this.isDeleting(pair)) return
      const isLoenaendring = pair.kind === 'loenaendring'
      const label = isLoenaendring ? 'lønbrøk' : 'faste tillæg'
      this.deletingKeys.push(pair.vRowKey)
      let pending = pair.ids.length
      let hadError = false
      const done = () => {
        if (pending > 0) return
        this.deletingKeys = this.deletingKeys.filter(k => k !== pair.vRowKey)
        if (hadError) {
          eventbus.toast = {text: 'Kunne ikke slette ' + (isLoenaendring ? 'lønbrøken' : 'det faste tillæg'), success: false, position: 'bottomLeft'}
          this.readTillaeger()
          return
        }
        if (isLoenaendring) {
          this.loenaendringer = this.loenaendringer.filter(r => pair.ids.indexOf(r.id) === -1)
        } else {
          this.tillaeger = this.tillaeger.filter(r => pair.ids.indexOf(r.id) === -1)
        }
        eventbus.toast = {text: (isLoenaendring ? 'Lønbrøken' : 'Det faste tillæg') + ' blev slettet fra udbakken', success: true, position: 'bottomLeft'}
      }
      const deleteFn = isLoenaendring ? docendo.deleteSilkeborgDataLoenaendring : docendo.deleteSilkeborgDataTillaeg
      pair.ids.forEach(id => {
        deleteFn.call(docendo, this.schoolId, id, () => {
          pending--
          done()
        }, failure => {
          console.log('delete ' + label + ' failure', failure)
          pending--
          hadError = true
          done()
        })
      })
    },
    formatDecimal (value) {
      if (value == null || value === '') return ''
      const num = Number(value)
      if (isNaN(num)) return String(value).replace('.', ',')
      // Afrund til maks. 3 decimaler og fjern overflødige nuller, så vi undgår
      // lange flydende-komma-cifre som fx 2,316666666666664.
      const rounded = Math.round(num * 1000) / 1000
      return String(rounded).replace('.', ',')
    },
    stateLabel (state) {
      switch (state) {
        case 'draft': return 'I udbakke'
        case 'sending': return 'Sender'
        case 'sent': return 'Sendt'
        default: return state || ''
      }
    },
    // Henter både faste tillæg og lønbrøker (SD-lønændringer), så begge typer vises samlet
    // i udbakke/afsendt. Lønbrøk gemmes som en lønændring, ikke et tillæg, så de kommer fra
    // hvert sit endpoint.
    readTillaeger () {
      this.isLoading = true
      let pending = 2
      const done = () => {
        pending--
        if (pending <= 0) this.isLoading = false
      }
      docendo.listSilkeborgDataTillaeg(this.schoolId, success => {
        if (success && success.status && success.status === 'error') {
          this.isNoConfiguration = true
        }
        if (Array.isArray(success)) {
          this.tillaeger = success
        } else if (success && Array.isArray(success.data)) {
          this.tillaeger = success.data
        } else {
          this.tillaeger = []
        }
        done()
      }, failure => {
        console.log(failure)
        this.tillaeger = []
        if (failure && failure.status && failure.status === 'error') {
          this.isNoConfiguration = true
        }
        done()
      })
      docendo.listSilkeborgDataLoenaendringer(this.schoolId, success => {
        if (success && success.status && success.status === 'error') {
          this.isNoConfiguration = true
        }
        if (Array.isArray(success)) {
          this.loenaendringer = success
        } else if (success && Array.isArray(success.data)) {
          this.loenaendringer = success.data
        } else {
          this.loenaendringer = []
        }
        done()
      }, failure => {
        console.log(failure)
        this.loenaendringer = []
        done()
      })
    },
    selectBeregnOption (option) {
      if (option.id === 'tidsbegraenset_uv') {
        this.isShowTidsbegraenset = true
        return
      }
      this.beregnType = option.id
      this.sdApproveState = {count: 0, isApproving: false, canApprove: false, missingEmployment: false}
      this.isShowBeregnModal = true
    },
    triggerSendToSd () {
      this.sendToSdTrigger++
    },
    closeBeregnModal () {
      this.isShowBeregnModal = false
      this.beregnType = null
      // Beregningen kan have sendt tillæg til udbakken; genindlæs så listen er opdateret.
      this.readTillaeger()
    }
  },
  watch: {
    selectedEmploymentIds (ids) {
      if (!ids || ids.length === 0) this.isBatchMode = false
    }
  },
  mounted () {
    this.$store.dispatch('getUsersFromSchoolId', this.schoolId)
    this.$store.dispatch('readEmployments')
    this.$store.dispatch('readSalaryCodes')
    this.readTillaeger()
  }
}
</script>

<style scoped>
@import "../../../assets/css/variables.css";

.sd-faste-tillaeg {
  padding: 1rem;
}

.sd-faste-tillaeg__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}

.sd-faste-tillaeg__header__left {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.sd-faste-tillaeg__header__right {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  margin-left: auto;
}

.sd-faste-tillaeg__toolbar {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.sd-faste-tillaeg__toolbar__wrap {
  display: inline-flex;
}

.sd-faste-tillaeg__count {
  font-style: italic;
  font-size: 11px;
  color: #6b7280;
}

.sd-faste-tillaeg__info {
  font-size: 12px;
  font-style: italic;
  color: #6b7280;
  margin-bottom: 0.75rem;
}

.do-table {
  width: 100%;
  border-collapse: collapse;
}

.do-table thead th {
  text-transform: uppercase;
  font-size: 10px;
  color: #8898aa;
  text-align: left;
  padding: 0.5rem;
  border-bottom: 1px solid #e2e8f0;
}

.do-table th.sortable {
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}

.do-table th.sortable:hover {
  color: var(--primary, #468AD7);
}

.sort-icon {
  font-size: 11px;
  color: inherit;
}

.do-table tbody td {
  padding: 0.5rem;
  border-bottom: 1px solid #f1f5f9;
}

.do-table tbody tr {
  transition: background-color 0.15s;
}

.do-table tbody tr:hover {
  background-color: aliceblue;
}

.do-table tbody.user-group:nth-of-type(2n) {
  background-color: #f9fafb;
}

.do-table tbody.user-group tr {
  cursor: pointer;
}

.do-table--striped tbody tr:nth-child(even) {
  background-color: #f9fafb;
}

.td-check {
  width: 26px;
}

.do-table thead th.td-check {
  border-right: 1px solid #e2e8f0;
}

.do-table tbody td.td-check {
  vertical-align: middle;
}

.do-table__toggle-all {
  color: var(--primary, #468ad7);
  cursor: pointer;
  user-select: none;
  text-transform: none;
  white-space: nowrap;
}

.do-table__toggle-all:hover {
  text-decoration: underline;
}

.do-table tbody tr.is-selected {
  background-color: #eaf3fb;
}

.do-checkbox {
  border: 1px solid #ccc;
  border-radius: .25rem;
  height: 1rem;
  position: relative;
  width: 1rem;
  cursor: pointer;
  display: inline-block;
  vertical-align: middle;
}

.do-checkbox.do-checkbox--selected {
  background: var(--primary);
  border-color: var(--primary);
}

.do-checkbox.do-checkbox--selected:after,
.do-checkbox.do-checkbox--selected:before {
  background: #fff;
}

.do-checkbox:after,
.do-checkbox:before {
  background: transparent;
  transform: rotate(35deg);
}

.do-checkbox:after {
  height: 8px;
  right: 3px;
  top: 1px;
  width: 1.5px;
}

.do-checkbox:after,
.do-checkbox:before {
  border-radius: 1rem;
  content: "";
  display: block;
  position: absolute;
}

.do-checkbox.do-checkbox--selected:before {
  bottom: 2.5px;
  height: 1.5px;
  right: 5px;
  width: 4px;
}

.td-organisation {
  opacity: 0.7;
  font-size: 11px;
}

.th-row-actions {
  width: 160px;
}

.td-row-actions {
  width: 160px;
  text-align: right;
  position: relative;
}

.do-bttn.do-bttn--small {
  font-size: 11px;
  height: auto;
  padding: 5px 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.row-action-btn {
  opacity: 0;
  pointer-events: none;
  white-space: nowrap;
  transition: opacity 0.12s ease;
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
}

.fast-tillaeg-row:hover .row-action-btn {
  opacity: 1;
  pointer-events: auto;
}

.td-date {
  width: 100px;
}

.td-units {
  text-align: right;
  width: 90px;
}

.do-table thead th.td-units {
  text-align: right;
}

.td-created {
  width: 150px;
}

.td-created-by {
  width: 140px;
}

.capitalize {
  text-transform: capitalize;
}

.td-status {
  width: 110px;
}

.th-actions, .td-actions {
  width: 90px;
  text-align: right;
}

.status-pill {
  display: inline-block;
  padding: 2px 8px;
  line-height: 1.5;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.status-pill--draft {
  background-color: #f3f4f6;
  color: #6b7280;
}

.status-pill--sending {
  background-color: #fef9c3;
  color: #854d0e;
}

.status-pill--sent {
  background-color: #dcfce7;
  color: #166534;
}

.beregn-modal {
  width: calc(100vw - 32px);
  height: calc(100vh - 120px);
  background: #f6f9fc;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.beregn-modal__header {
  background: #fff;
  padding: 1rem;
  border-bottom: 1px solid #d8d8d8;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
}

.beregn-modal__header h2 {
  margin: 0;
}

.beregn-modal__close {
  cursor: pointer;
  font-size: 1.2rem;
  opacity: 0.85;
}

.beregn-modal__close:hover {
  opacity: 1;
}

.beregn-modal__content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 1rem;
}

.beregn-modal__footer {
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: #fff;
  border-top: 1px solid #d8d8d8;
}

.send-to-sd-wrap {
  display: inline-flex;
}

.beregn-menu {
  list-style: none;
  margin: 0;
  padding: 0;
  max-width: 340px;
}

.beregn-menu li {
  white-space: normal;
  line-height: 1.3;
}
</style>



// WEBPACK FOOTER //
// SdFasteTillaeg.vue?06c8911c